import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Navigation, Layers, Locate, X, Star, Clock, Car, Route } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { placesApi } from '../services/api';

// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (isSelected, category) => {
  const color = isSelected ? '#ef4444' : getCategoryColor(category);
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        background: ${color};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        border: 3px solid white;
      ">
        <svg style="transform: rotate(45deg); width: 18px; height: 18px; color: white;" viewBox="0 0 24 24" fill="currentColor" stroke="none">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

const getCategoryColor = (category) => {
  const cat = (category || '').toLowerCase();
  if (cat.includes('tarihi') || cat.includes('kale')) return '#8B4513';
  if (cat.includes('doğal') || cat.includes('şelale') || cat.includes('tabiat')) return '#228B22';
  if (cat.includes('yayla')) return '#2E8B57';
  if (cat.includes('plaj') || cat.includes('deniz') || cat.includes('ada')) return '#1E90FF';
  if (cat.includes('müze')) return '#9932CC';
  if (cat.includes('göl')) return '#00CED1';
  return '#1e3a5f';
};

// Giresun merkez koordinatları
const GIRESUN_CENTER = [40.9128, 38.3895];
const DEFAULT_ZOOM = 11;

// Giresun'daki önemli yerlerin GERÇEK koordinatları (Google Maps'ten doğrulanmış)
const PLACE_COORDINATES = {
  // Merkez
  'Giresun Kalesi': [40.9216, 38.3892],
  'Giresun Müzesi': [40.9175, 38.3872],
  'Giresun Adası': [40.9289, 38.4361],
  
  // Sahil ve Plajlar
  'Zeytinlik Plajı': [40.9150, 38.4100],
  'Zeytinlik Mahallesi Plajı': [40.9150, 38.4100],
  'Bulancak Sahili': [40.9380, 38.2350],
  
  // Şelaleler ve Doğa
  'Kuzalan Şelalesi': [40.6850, 38.5200],
  'Kuzalan Şelalesi Tabiat Parkı': [40.6850, 38.5200],
  'Kuzalan Tabiat Parkı': [40.6850, 38.5200],
  'Mavi Göl': [40.6750, 38.5100],
  'Aksu Deresi Vadisi': [40.7500, 38.4000],
  
  // Yaylalar
  'Kulakkaya Yaylası': [40.6995, 38.3394],
  'Bektaş Yaylası': [40.6200, 38.3500],
  'Kümbet Yaylası': [40.5800, 38.4000],
  'Dereli Kümbet Yaylası': [40.5800, 38.4000],
  
  // Kaleler
  'Tirebolu Kalesi': [41.0050, 38.8150],
  'Espiye Andoz Kalesi': [40.9480, 38.7100],
  'Şebinkarahisar Kalesi': [40.2870, 38.4200],
  
  // İlçeler
  'Tirebolu': [41.0000, 38.8167],
  'Espiye': [40.9500, 38.7000],
  'Görele': [41.0333, 39.0000],
  'Bulancak': [40.9380, 38.2350],
  'Dereli': [40.6167, 38.4000],
  'Keşap': [40.9667, 38.5000],
  
  // Varsayılan (Giresun merkez)
  'default': [40.9128, 38.3895]
};

// Component to handle map events
const MapController = ({ selectedPlace }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedPlace) {
      const coords = getPlaceCoordinates(selectedPlace.name);
      map.flyTo(coords, 14, { duration: 1.5 });
    }
  }, [selectedPlace, map]);
  
  return null;
};

const getPlaceCoordinates = (placeName) => {
  if (!placeName) return GIRESUN_CENTER;
  
  // Check exact match first
  if (PLACE_COORDINATES[placeName]) {
    return PLACE_COORDINATES[placeName];
  }
  
  // Check partial match
  const placeNameLower = placeName.toLowerCase();
  for (const [key, coords] of Object.entries(PLACE_COORDINATES)) {
    const keyLower = key.toLowerCase();
    if (placeNameLower.includes(keyLower) || keyLower.includes(placeNameLower)) {
      return coords;
    }
  }
  
  // Check for keywords
  if (placeNameLower.includes('kale')) {
    return PLACE_COORDINATES['Giresun Kalesi'];
  }
  if (placeNameLower.includes('şelale') || placeNameLower.includes('kuzalan')) {
    return PLACE_COORDINATES['Kuzalan Şelalesi'];
  }
  if (placeNameLower.includes('yayla') || placeNameLower.includes('kulakkaya')) {
    return PLACE_COORDINATES['Kulakkaya Yaylası'];
  }
  if (placeNameLower.includes('ada') || placeNameLower.includes('giresun adası')) {
    return PLACE_COORDINATES['Giresun Adası'];
  }
  if (placeNameLower.includes('göl') || placeNameLower.includes('mavi')) {
    return PLACE_COORDINATES['Mavi Göl'];
  }
  
  // Return default with slight random offset to avoid overlapping
  const offset = (Math.random() - 0.5) * 0.05;
  return [GIRESUN_CENTER[0] + offset, GIRESUN_CENTER[1] + offset];
};

const MapPage = () => {
  const { darkMode } = useTheme();
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapStyle, setMapStyle] = useState('streets');

  // Dark mode classes
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const data = await placesApi.getAll();
        // Add coordinates to each place
        const placesWithCoords = data.map(place => ({
          ...place,
          coordinates: getPlaceCoordinates(place.name)
        }));
        setPlaces(placesWithCoords);
      } catch (error) {
        console.error('Error fetching places:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlaces();
  }, []);

  // Different map tile layers
  const getTileLayer = () => {
    switch(mapStyle) {
      case 'satellite':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: '&copy; Esri'
        };
      case 'terrain':
        return {
          url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
          attribution: '&copy; OpenTopoMap'
        };
      default: // streets
        return {
          url: darkMode 
            ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
            : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
          attribution: '&copy; <a href="https://carto.com/">CARTO</a>'
        };
    }
  };

  const cycleMapStyle = () => {
    setMapStyle(prev => {
      if (prev === 'streets') return 'satellite';
      if (prev === 'satellite') return 'terrain';
      return 'streets';
    });
  };

  const openGoogleMaps = (place) => {
    const coords = place.coordinates || getPlaceCoordinates(place.name);
    const url = `https://www.google.com/maps/dir/?api=1&destination=${coords[0]},${coords[1]}&travelmode=driving`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className={`h-full min-h-[500px] ${darkMode ? 'bg-slate-900' : 'bg-gray-100'} flex items-center justify-center`}>
        <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tileLayer = getTileLayer();

  return (
    <div className={`w-full ${darkMode ? 'bg-slate-900' : 'bg-gray-100'} relative`} style={{ height: 'calc(100vh - 140px)', minHeight: '500px' }}>
      {/* Map Container */}
      <MapContainer
        center={GIRESUN_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
        zoomControl={true}
      >
        <TileLayer
          key={mapStyle + darkMode}
          attribution={tileLayer.attribution}
          url={tileLayer.url}
        />
        
        <MapController selectedPlace={selectedPlace} />
        
        {/* Place Markers */}
        {places.map((place) => (
          <Marker
            key={place.id}
            position={place.coordinates}
            icon={createCustomIcon(selectedPlace?.id === place.id, place.category_name)}
            eventHandlers={{
              click: () => setSelectedPlace(place),
            }}
          >
            <Popup className="custom-popup">
              <div className="min-w-[220px] p-1">
                <img src={place.image} alt={place.name} className="w-full h-28 object-cover rounded-lg mb-2" />
                <h3 className="font-bold text-gray-900 text-base">{place.name}</h3>
                <p className="text-xs text-gray-500 mt-1">{place.category_name}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="flex items-center text-xs text-yellow-600">
                    <Star className="w-3 h-3 fill-yellow-400 mr-1" />
                    {place.rating}
                  </span>
                  <span className="text-xs text-gray-500">{place.distance}</span>
                </div>
                <button 
                  onClick={() => openGoogleMaps(place)}
                  className="w-full mt-3 py-2 bg-teal-500 text-white text-xs rounded-lg font-medium hover:bg-teal-600 transition-colors flex items-center justify-center gap-1"
                >
                  <Navigation className="w-3 h-3" />
                  Google Maps'te Aç
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Map Controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-[1000]">
        <button 
          onClick={cycleMapStyle}
          className={`w-11 h-11 ${bgCard} rounded-xl shadow-lg flex items-center justify-center ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} transition-colors`}
          title={`Harita Stili: ${mapStyle === 'streets' ? 'Sokak' : mapStyle === 'satellite' ? 'Uydu' : 'Arazi'}`}
        >
          <Layers className={`w-5 h-5 ${textPrimary}`} />
        </button>
        <button 
          onClick={() => {
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition((pos) => {
                // Could fly to user location here
              });
            }
          }}
          className="w-11 h-11 bg-teal-500 rounded-xl shadow-lg flex items-center justify-center hover:bg-teal-600 transition-colors"
          title="Konumuma Git"
        >
          <Locate className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Map Style Indicator */}
      <div className={`absolute top-4 left-1/2 -translate-x-1/2 ${bgCard} rounded-full shadow-lg px-4 py-2 z-[1000]`}>
        <span className={`text-sm font-medium ${textPrimary}`}>
          {mapStyle === 'streets' ? '🗺️ Sokak Haritası' : mapStyle === 'satellite' ? '🛰️ Uydu Görünümü' : '⛰️ Arazi Haritası'}
        </span>
      </div>

      {/* Places Count Badge */}
      <div className={`absolute top-4 left-4 ${bgCard} rounded-xl shadow-lg px-4 py-2.5 z-[1000]`}>
        <div className="flex items-center gap-2">
          <MapPin className={`w-5 h-5 text-teal-500`} />
          <span className={`font-semibold ${textPrimary}`}>{places.length}</span>
          <span className={`text-sm ${textSecondary}`}>Mekan</span>
        </div>
      </div>

      {/* Selected Place Card */}
      {selectedPlace && (
        <div className={`absolute bottom-4 left-4 right-4 max-w-lg mx-auto ${bgCard} rounded-2xl shadow-2xl p-4 z-[1000]`}>
          <button
            onClick={() => setSelectedPlace(null)}
            className={`absolute top-3 right-3 p-1.5 rounded-full ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
          >
            <X className={`w-4 h-4 ${textSecondary}`} />
          </button>
          
          <div className="flex gap-4">
            <img
              src={selectedPlace.image}
              alt={selectedPlace.name}
              className="w-32 h-32 rounded-xl object-cover"
            />
            <div className="flex-1 min-w-0">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/50 text-teal-400' : 'bg-teal-50 text-teal-700'}`}>
                {selectedPlace.category_name}
              </span>
              <h3 className={`font-bold ${textPrimary} mt-1.5 text-lg leading-tight`}>{selectedPlace.name}</h3>
              <p className={`text-sm ${textSecondary} mt-1 line-clamp-2`}>{selectedPlace.description}</p>
              
              <div className="flex items-center gap-4 mt-2">
                <span className="flex items-center text-sm">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                  <span className="font-medium">{selectedPlace.rating}</span>
                </span>
                <span className={`flex items-center text-sm ${textSecondary}`}>
                  <Route className="w-4 h-4 mr-1" />
                  {selectedPlace.distance}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <button 
              onClick={() => openGoogleMaps(selectedPlace)}
              className="flex-1 py-2.5 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors flex items-center justify-center gap-2"
            >
              <Navigation className="w-4 h-4" />
              Yol Tarifi Al
            </button>
            <button className={`py-2.5 px-5 rounded-xl font-medium border ${darkMode ? 'border-slate-600 text-white hover:bg-slate-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors flex items-center justify-center gap-2`}>
              <Car className="w-4 h-4" />
              Rotaya Ekle
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPage;
